<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { 
  NCard,
  NButton,
  NSelect,
  NForm,
  NFormItem,
  NInput,
  NAlert,
  NDivider,
  NSpace,
  NSpin,
  NIcon,
  NModal
} from 'naive-ui';
import { 
  Key,
  Shield,
  Eye,
  EyeOff,
  Copy
} from 'lucide-vue-next';

import { updateLanguage, authToken, isClient } from '@/services/auth';
import { LANGUAGES, getPreferredLanguage, savePreferredLanguage, getLanguageLabel } from '@/services/language';
import { fetchMyClientProfile, recoverClientPassword } from '@/services/client';
import { buildQuestionHashMap, getQuestionFromHash, verifySecurityAnswer, 
  derivePasswordFromSecurity, decryptClientData } from '@/services/encryption';

// Language settings (existing)
const currentLanguage = ref(getPreferredLanguage() || 'english');
const selectedLanguage = ref(currentLanguage.value);
const isLoading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Client recovery state
const clientData = ref(null);
const recoveryData = ref(null); // Data from backend
const loadingClientData = ref(false);
const recoveryAnswers = ref({
  answer1: "",
  answer2: "",
  answer3: ""
});
const recoveryError = ref(null);
const isRecovering = ref(false);
const recoveredPassword = ref("");
const showRecoveredPassword = ref(false);
const showPassword = ref(false);

// Recovery attempts tracking
const recoveryAttempts = ref(0);
const isBlocked = ref(false);
const blockExpiresAt = ref(null);
const timeRemaining = ref(0);

// Question hash mapping
const questionHashMap = ref({});

// Function to get readable question from hash
const getQuestionText = (questionHash) => {
  return getQuestionFromHash(questionHash, questionHashMap.value);
};

// Compute if there are changes to save
const hasChanged = computed(() => {
  return selectedLanguage.value !== currentLanguage.value;
});

// Check if currently blocked and update timer
const checkBlockStatus = () => {
  if (!blockExpiresAt.value) {
    isBlocked.value = false;
    timeRemaining.value = 0;
    return;
  }

  const now = Date.now();
  if (now >= blockExpiresAt.value) {
    // Block expired
    isBlocked.value = false;
    blockExpiresAt.value = null;
    timeRemaining.value = 0;
    recoveryAttempts.value = 0;
    localStorage.removeItem('recovery_attempts');
    localStorage.removeItem('block_expires_at');
  } else {
    // Still blocked
    isBlocked.value = true;
    timeRemaining.value = Math.ceil((blockExpiresAt.value - now) / 1000);
  }
};

// Format time remaining
const formatTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Load recovery attempt data from localStorage
const loadRecoveryState = () => {
  const attempts = localStorage.getItem('recovery_attempts');
  const blockExpires = localStorage.getItem('block_expires_at');
  
  if (attempts) {
    recoveryAttempts.value = parseInt(attempts);
  }
  
  if (blockExpires) {
    blockExpiresAt.value = parseInt(blockExpires);
    checkBlockStatus();
  }
};

// Save recovery attempt data to localStorage
const saveRecoveryState = () => {
  localStorage.setItem('recovery_attempts', recoveryAttempts.value.toString());
  if (blockExpiresAt.value) {
    localStorage.setItem('block_expires_at', blockExpiresAt.value.toString());
  }
};

// Increment failed attempts
const incrementFailedAttempts = () => {
  recoveryAttempts.value++;
  
  if (recoveryAttempts.value >= 3) {
    // Block for 10 minutes
    blockExpiresAt.value = Date.now() + (10 * 60 * 1000);
    isBlocked.value = true;
    recoveryError.value = "Too many failed attempts. Please try again in 10 minutes.";
  }
  
  saveRecoveryState();
};

// Reset attempts on successful recovery
const resetRecoveryAttempts = () => {
  recoveryAttempts.value = 0;
  isBlocked.value = false;
  blockExpiresAt.value = null;
  timeRemaining.value = 0;
  localStorage.removeItem('recovery_attempts');
  localStorage.removeItem('block_expires_at');
};

// Load client data for recovery questions
const loadClientData = async () => {
  if (!isClient.value) return;
  
  try {
    loadingClientData.value = true;
    const profileData = await fetchMyClientProfile(authToken.value);
    
    if (profileData) {
      clientData.value = profileData;
    }
  } catch (err) {
    console.error('Failed to load client data:', err);
  } finally {
    loadingClientData.value = false;
  }
};

// Get recovery data from backend
const getRecoveryData = async () => {
  try {
    const response = await recoverClientPassword(authToken.value);
    recoveryData.value = response;
    return response;
  } catch (err) {
    console.error('Failed to get recovery data:', err);
    throw err;
  }
};

// Verify answers and decrypt password
const verifyAnswersAndDecrypt = async (answers, recoveryData) => {
  try {
    // Verify each answer matches what was stored
    const valid1 = await verifySecurityAnswer(answers.answer1, recoveryData.security_answer_1_hash);
    const valid2 = await verifySecurityAnswer(answers.answer2, recoveryData.security_answer_2_hash);
    const valid3 = await verifySecurityAnswer(answers.answer3, recoveryData.security_answer_3_hash);

    if (!(valid1 && valid2 && valid3)) {
      return { success: false, error: "Incorrect security answers" };
    }

    // Recreate the security key (same as encryption time)
    const securityKey = await derivePasswordFromSecurity([
      answers.answer1, 
      answers.answer2, 
      answers.answer3
    ]);

    // NOW DECRYPT the encrypted password to get the original "Mum130brad898"
    const originalPassword = await decryptClientData(
      recoveryData.encrypted_recovery_password,
      recoveryData.recovery_encryption_metadata,  // Use the metadata
      securityKey
    );

    return { success: true, password: originalPassword };
  } catch (err) {
    console.error('Password decryption failed:', err);
    return { success: false, error: err.message };
  }
};

// Save the language preference
async function saveLanguagePreference() {
  if (!hasChanged.value) return;
  
  errorMessage.value = '';
  successMessage.value = '';
  isLoading.value = true;
  
  try {
    // Update language on the server
    const result = await updateLanguage(authToken.value, selectedLanguage.value);
    
    // Update local state using the same service as LanguageSwitcher
    currentLanguage.value = selectedLanguage.value;
    savePreferredLanguage(selectedLanguage.value);
    
    // No need to refresh - savePreferredLanguage already updates i18n and dispatches an event
    
    successMessage.value = 'Language preference updated successfully!';
  } catch (error) {
    console.error('Failed to update language:', error);
    errorMessage.value = error.message || 'Failed to update language preference. Please try again.';
  } finally {
    isLoading.value = false;
  }
}

// Recover client password
const recoverPassword = async () => {
  if (isBlocked.value) {
    recoveryError.value = `Recovery is blocked. Please try again in ${formatTimeRemaining.value}.`;
    return;
  }

  if (!recoveryAnswers.value.answer1.trim() || 
      !recoveryAnswers.value.answer2.trim() || 
      !recoveryAnswers.value.answer3.trim()) {
    recoveryError.value = "Please answer all security questions";
    return;
  }

  try {
    isRecovering.value = true;
    recoveryError.value = null;

    // Get recovery data from backend
    const backendData = await getRecoveryData();

    // Verify answers and decrypt password on frontend
    const result = await verifyAnswersAndDecrypt(recoveryAnswers.value, backendData);

    if (result.success) {
      // Success - reset attempts and show password
      resetRecoveryAttempts();
      recoveredPassword.value = result.password;
      showRecoveredPassword.value = true;

      // Clear answers for security
      recoveryAnswers.value = { answer1: "", answer2: "", answer3: "" };

      // Auto-hide after 2 minutes for security
      setTimeout(() => {
        hideRecoveredPassword();
      }, 120000);
    } else {
      // Failed verification
      incrementFailedAttempts();
      recoveryError.value = result.error;
    }

  } catch (err) {
    console.error('Password recovery failed:', err);
    incrementFailedAttempts();
    recoveryError.value = err.message || "Recovery failed. Please try again.";
  } finally {
    isRecovering.value = false;
  }
};

const hideRecoveredPassword = () => {
  showRecoveredPassword.value = false;
  recoveredPassword.value = "";
  showPassword.value = false;
};

const copyPassword = async () => {
  try {
    await navigator.clipboard.writeText(recoveredPassword.value);
    // Could show a toast notification here
  } catch (err) {
    console.error('Failed to copy password:', err);
  }
};

// Timer interval
let timerInterval = null;

// Start timer if blocked
const startTimer = () => {
  if (timerInterval) clearInterval(timerInterval);
  
  timerInterval = setInterval(() => {
    checkBlockStatus();
    
    if (!isBlocked.value && timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }, 1000);
};

// Load current preferences when component mounts
onMounted(async () => {
  selectedLanguage.value = getPreferredLanguage() || 'english';
  
  // Load recovery state
  loadRecoveryState();
  
  // Start timer if blocked
  if (isBlocked.value) {
    startTimer();
  }
  
  // Build question hash map
  questionHashMap.value = await buildQuestionHashMap();
  
  // Load client data if user is a client
  if (isClient.value) {
    loadClientData();
  }
});

// Cleanup timer on unmount
onBeforeUnmount(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});
</script>

<template>
  <div class="settings-container">
    <h1>Settings</h1>
    
    <!-- Language Preference Section -->
    <NCard title="Language Preference" class="settings-section">
      <NForm>
        <NFormItem label="Select your preferred language">
          <NSelect
            v-model:value="selectedLanguage"
            :options="LANGUAGES"
            :disabled="isLoading"
            placeholder="Choose language"
          />
        </NFormItem>
        
        <div v-if="errorMessage" class="error-message">
          <NAlert type="error" :title="errorMessage" closable />
        </div>
        
        <div v-if="successMessage" class="success-message">
          <NAlert type="success" :title="successMessage" closable />
        </div>
        
        <NButton 
          @click="saveLanguagePreference" 
          :loading="isLoading"
          :disabled="!hasChanged"
          type="primary"
        >
          Save Changes
        </NButton>
      </NForm>
    </NCard>

    <!-- Client Password Recovery Section -->
    <NCard v-if="isClient" title="Client Password Recovery" class="settings-section">
      <template #header-extra>
        <NIcon>
          <Shield />
        </NIcon>
      </template>
      
      <div v-if="loadingClientData" class="loading-state">
        <NSpin size="medium" />
        <p>Loading client data...</p>
      </div>
      
      <div v-else-if="!clientData">
        <NAlert type="info" title="No Client Profile">
          You don't have a client profile yet. Create a client profile to enable password recovery.
        </NAlert>
      </div>
      
      <div v-else>
        <p class="recovery-description">
          If you've forgotten your client password, answer your security questions below to recover it.
        </p>
        
        <!-- Block notice -->
        <div v-if="isBlocked" class="block-notice">
          <NAlert type="error" title="Too Many Failed Attempts">
            Recovery is temporarily blocked due to too many failed attempts. 
            Please try again in {{ formatTimeRemaining }}.
          </NAlert>
        </div>
        
        <!-- Attempts warning -->
        <div v-else-if="recoveryAttempts > 0" class="attempts-warning">
          <NAlert type="warning" :title="`Attempt ${recoveryAttempts}/3`">
            {{ 3 - recoveryAttempts }} attempts remaining before recovery is blocked for 10 minutes.
          </NAlert>
        </div>
        
        <NAlert type="warning" title="Security Notice" class="security-notice">
          Your recovered password will be shown temporarily and then hidden for security. Make sure you're in a private location.
        </NAlert>
        
        <!-- Show recovered password -->
        <div v-if="showRecoveredPassword" class="recovered-password-section">
          <NAlert type="success" title="Password Recovered Successfully!">
            <div class="password-display">
              <div class="password-field">
                <strong>Your Client Password:</strong>
                <div class="password-value">
                  <code>{{ showPassword ? recoveredPassword : '••••••••••••••••' }}</code>
                  <NSpace>
                    <NButton 
                      size="small" 
                      @click="showPassword = !showPassword"
                      type="primary"
                      text
                    >
                      <template #icon>
                        <NIcon>
                          <Eye v-if="!showPassword" />
                          <EyeOff v-else />
                        </NIcon>
                      </template>
                    </NButton>
                    
                    <NButton 
                      size="small" 
                      @click="copyPassword"
                      type="primary"
                      text
                    >
                      <template #icon>
                        <NIcon>
                          <Copy />
                        </NIcon>
                      </template>
                    </NButton>
                  </NSpace>
                </div>
              </div>
              
              <div class="password-instructions">
                <p><strong>Instructions:</strong></p>
                <ul>
                  <li>Copy this password now - it will be hidden automatically in 2 minutes</li>
                  <li>Use this password to access your encrypted client profile</li>
                  <li>Consider writing it down securely or using a password manager</li>
                </ul>
              </div>
              
              <NButton @click="hideRecoveredPassword" type="primary">
                Hide Password
              </NButton>
            </div>
          </NAlert>
        </div>
        
        <!-- Recovery form -->
        <div v-else>
          <NForm>
            <NFormItem :label="getQuestionText(clientData.security_question_1)">
              <NInput
                v-model:value="recoveryAnswers.answer1"
                type="password"
                placeholder="Enter your answer"
                :disabled="isRecovering || isBlocked"
                show-password-on="click"
              />
            </NFormItem>
            
            <NFormItem :label="getQuestionText(clientData.security_question_2)">
              <NInput
                v-model:value="recoveryAnswers.answer2"
                type="password"
                placeholder="Enter your answer"
                :disabled="isRecovering || isBlocked"
                show-password-on="click"
              />
            </NFormItem>
            
            <NFormItem :label="getQuestionText(clientData.security_question_3)">
              <NInput
                v-model:value="recoveryAnswers.answer3"
                type="password"
                placeholder="Enter your answer"
                :disabled="isRecovering || isBlocked"
                show-password-on="click"
              />
            </NFormItem>
            
            <div v-if="recoveryError" class="recovery-error">
              <NAlert type="error" :title="recoveryError" closable />
            </div>
            
            <NButton 
              @click="recoverPassword"
              :loading="isRecovering"
              :disabled="!recoveryAnswers.answer1.trim() || !recoveryAnswers.answer2.trim() || !recoveryAnswers.answer3.trim() || isBlocked"
              type="primary"
            >
              <template #icon>
                <NIcon>
                  <Key />
                </NIcon>
              </template>
              Recover Password
            </NButton>
          </NForm>
        </div>
      </div>
    </NCard>
  </div>
</template>

<style scoped>
.settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.settings-section {
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
}

.recovery-description {
  margin-bottom: 16px;
  color: #666;
  font-size: 0.95rem;
}

.block-notice,
.attempts-warning,
.security-notice {
  margin-bottom: 20px;
}

.recovered-password-section {
  margin-bottom: 20px;
}

.password-display {
  padding: 16px 0;
}

.password-field {
  margin-bottom: 16px;
}

.password-value {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.password-value code {
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 14px;
  background: transparent;
  padding: 0;
  color: #333;
  flex: 1;
  word-break: break-all;
}

.password-instructions {
  margin-bottom: 20px;
  padding: 12px;
  background-color: #f0f7ff;
  border-radius: 6px;
  border: 1px solid #d6e3ff;
}

.password-instructions p {
  margin: 0 0 8px 0;
  font-weight: 500;
  color: #0e639c;
}

.password-instructions ul {
  margin: 0;
  padding-left: 16px;
  color: #666;
}

.password-instructions li {
  margin-bottom: 4px;
  font-size: 0.9rem;
}

.recovery-error {
  margin-bottom: 16px;
}

.error-message,
.success-message {
  margin-bottom: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .settings-container {
    padding: 10px;
  }
  
  .password-value {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}
</style>